import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Provider } from 'react-redux';
import { init } from '@rematch/core';
import FireInsuranceBudget from '../../fire-insurance/budget';
import { AvailableBrokersAndEstatesState } from '../../../store/types/temp-types';
import { getFireInsuranceActiveInsurers } from '../../../services/insurer';
import { dangerNotification } from '../../../services/notification';
import { IframeContainer, ErrorContainer, LoadingContainer } from './styles';
import { validateUser } from '../../../services/oauth';
import { set } from '../../../services/storage';
import { BrokerForGetBrokerResponseDTO } from '../../../dtos/broker/broker-for-get-broker-response.dto';
import { getBroker, getBrokerObjectFromGetBrokerDTO } from '../../../services/broker';
import { BrokerState } from '../../../types/BrokerState';
import {
  BrokersToCustomDropDownOptions,
  EstatesToCustomDropDownOptions
} from '../../../util';
import { maskCpfOrCnpj, maskPhoneNumber, maskZipCode } from '../../../services/masks';

export type InitialAddressType = {
  street: string | null;
  number: string | null;
  complement: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
};

export type InitialTenantType = {
  name: string | null;
  document: string | null;
  birthdate: string | null;
  email: string | null;
  phoneNumber: string | null;
};

export type InitialInsuredType = {
  name: string | null;
  document: string | null;
  email: string | null;
  phoneNumber: string | null;
};

const FireInsuranceBudgetIframe: React.FC = () => {
  const location = useLocation();
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [availableBrokersAndEstates, setAvailableBrokersAndEstates] =
    useState<AvailableBrokersAndEstatesState | null>(null);
  const [user, setUser] = useState<any>(null);
  const [store, setStore] = useState<any>(null);
  const [rentValue, setRentValue] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);

  const [address, setAddress] = useState<InitialAddressType>();
  const [tenant, setTenant] = useState<InitialTenantType>();
  const [insured, setInsured] = useState<InitialInsuredType>();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const accessToken = queryParams.get('token');
    const rentValueParam = queryParams.get('rentValue');
    const startDateParam = queryParams.get('startDate');

    /// endereço do imóvel
    const street = queryParams.get('street');
    const number = queryParams.get('number');
    const complement = queryParams.get('complement');
    const neighborhood = queryParams.get('neighborhood');
    const city = queryParams.get('city');
    const state = queryParams.get('state');
    const zipCode = queryParams.get('zipCode');

    setAddress({
      street,
      number,
      complement,
      neighborhood,
      city,
      state,
      zipCode: zipCode ? maskZipCode(zipCode) : null
    });

    // Dados do inquilino
    const tenantName = queryParams.get('tenantName');
    const tenantDocument = queryParams.get('tenantDocument');
    const tenantBirthDate = queryParams.get('tenantBirthDate');
    const tenantEmail = queryParams.get('tenantEmail');
    const tenantPhone = queryParams.get('tenantPhone');

    setTenant({
      name: tenantName,
      document: tenantDocument ? maskCpfOrCnpj(tenantDocument) : null,
      birthdate: tenantBirthDate,
      email: tenantEmail,
      phoneNumber: tenantPhone ? maskPhoneNumber(tenantPhone) : null
    });

    // Dados do proprietário
    const insuredName = queryParams.get('insuredName');
    const insuredDocument = queryParams.get('insuredDocument');
    const insuredEmail = queryParams.get('insuredEmail');
    const insuredPhone = queryParams.get('insuredPhone');

    setInsured({
      name: insuredName,
      document: insuredDocument,
      email: insuredEmail,
      phoneNumber: insuredPhone ? maskPhoneNumber(insuredPhone) : null
    });

    if (rentValueParam) {
      setRentValue(parseFloat(rentValueParam));
    }

    if (startDateParam) {
      setStartDate(startDateParam);
    }

    if (accessToken) {
      setToken(accessToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      fetchUserData(accessToken);
    } else {
      setLoading(false);
      dangerNotification('Erro', 'Token de acesso não fornecido');
    }
  }, [location]);

  const fetchUserData = async (accessToken: string) => {
    try {
      setLoading(true);

      const response = await validateUser(accessToken);

      if (response) {
        setUser(response);
        set('id_token', accessToken);

        const estateId = response.estate?.id;

        const brokerDTO: BrokerForGetBrokerResponseDTO = await getBroker(
          '7c98d9d9-a975-45a3-9131-cc1961250182'
        );

        const broker: BrokerState | null = brokerDTO
          ? getBrokerObjectFromGetBrokerDTO(brokerDTO)
          : null;

        const estates = broker?.estates;

        if (estateId) {
          try {
            const insurersResponse = await getFireInsuranceActiveInsurers(estateId);

            const newAvailableBrokersAndEstates = {
              estates: EstatesToCustomDropDownOptions(true, estates || []),
              insurers: insurersResponse.fire_insurance_active_insurers.map(
                (insurer: any) => ({
                  key: insurer.id,
                  text: insurer.name,
                  value: insurer,
                  image: insurer.image_url
                })
              ),
              brokers: BrokersToCustomDropDownOptions(true, broker ? [broker] : []),
              roles: [],
              franchises: [],
              multiFranchises: [],
              bondInsurers: [],
              enableFireInsuranceMenu: true
            };

            setAvailableBrokersAndEstates(newAvailableBrokersAndEstates);

            const models = {
              user: {
                state: response,
                reducers: {
                  updateUser: (state: any, payload: any) => payload
                }
              },
              availableBrokersAndEstates: {
                state: newAvailableBrokersAndEstates,
                reducers: {
                  updateAvailableBrokersAndEstates: (state: any, payload: any) => payload
                }
              },
              scroll: {
                state: {},
                reducers: {
                  updateScroll: (state: any, payload: any) => payload
                }
              },
              insurers: {
                state: insurersResponse.fire_insurance_active_insurers || [],
                reducers: {
                  updateInsurers: (state: any, payload: any) => payload
                }
              },
              currentPage: {
                state: {},
                reducers: {
                  updateCurrentPage: (state: any, payload: any) => payload
                }
              },
              toggledMenu: {
                state: { toggled: false },
                reducers: {
                  toggle: (state: any) => ({ toggled: !state.toggled }),
                  set: (state: any, payload: boolean) => ({ toggled: payload })
                }
              }
            };

            const newStore = init({
              models
            });

            setStore(newStore);
          } catch (error) {
            console.error('Error fetching insurers:', error);
            dangerNotification('Erro', 'Falha ao carregar seguradoras disponíveis');
          }
        }
      }
    } catch (error) {
      console.error('Authentication error:', error);
      dangerNotification('Erro de autenticação', 'Token inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const configureMessageListener = () => {
    window.addEventListener('message', event => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const { type, data } = event.data;
      if (type === 'SET_RENT_VALUE' && data) {
        setRentValue(parseFloat(data));
      } else if (type === 'SET_START_DATE' && data) {
        setStartDate(data);
      }
    });
  };

  useEffect(() => {
    configureMessageListener();
  }, []);

  if (loading) {
    return (
      <LoadingContainer>
        <p>Carregando...</p>
      </LoadingContainer>
    );
  }

  if (!token || !user || !availableBrokersAndEstates || !store) {
    return (
      <ErrorContainer>
        <p>Acesso não autorizado ou dados indisponíveis</p>
      </ErrorContainer>
    );
  }

  return (
    <IframeContainer>
      <Provider store={store}>
        <FireInsuranceBudget
          initialRentValue={rentValue}
          initialStartDate={startDate}
          initialAddress={address}
          initialTenant={tenant}
          initialInsured={insured}
          isIframe={true}
          onBudgetCreated={handleBudgetCreated}
        />
      </Provider>
    </IframeContainer>
  );

  function handleBudgetCreated(fireInsuranceId: string) {
    if (window.parent) {
      window.parent.postMessage(
        {
          type: 'BUDGET_CREATED',
          data: {
            fireInsuranceId: fireInsuranceId
          }
        },
        '*'
      );
    }
  }
};

export default FireInsuranceBudgetIframe;
